import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { map, Observable, ReplaySubject, switchMap, take, tap } from 'rxjs';
import { UserRole } from '../user/user.types';
import { UserService } from '../user/user.service';
import { Navigation } from './navigation.types';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private _httpClient = inject(HttpClient);
  private _userService = inject(UserService);
  private _navigation: ReplaySubject<Navigation> =
    new ReplaySubject<Navigation>(1);

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for navigation
   */
  get navigation$(): Observable<Navigation> {
    return this._navigation.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get(): Observable<Navigation> {
    return this._httpClient.get<Navigation>('api/common/navigation').pipe(
      switchMap((navigation) =>
        this._userService.user$.pipe(
          take(1),
          map((user) => {
            const role = user?.role;
            return {
              compact: filterByRole(navigation.compact, role),
              default: filterByRole(navigation.default, role),
              futuristic: filterByRole(navigation.futuristic, role),
              horizontal: filterByRole(navigation.horizontal, role),
            };
          }),
        ),
      ),
      tap((navigation) => {
        this._navigation.next(navigation);
      }),
    );
  }
}

/**
 * Drops any item (and its children) whose `meta.requiredRoles` the current
 * user doesn't hold - the one place nav visibility is decided, so every
 * layout variant (compact/default/futuristic/horizontal) stays in sync.
 */
function filterByRole(
  items: FuseNavigationItem[],
  role: UserRole | undefined,
): FuseNavigationItem[] {
  return items
    .filter((item) => {
      const requiredRoles = item.meta?.requiredRoles as UserRole[] | undefined;
      return !requiredRoles?.length || (!!role && requiredRoles.includes(role));
    })
    .map((item) =>
      item.children
        ? { ...item, children: filterByRole(item.children, role) }
        : item,
    );
}
